import {Divider, Stack, Text} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import * as ComputedFieldUtil from 'schemas/fields/computed/ComputedFieldUtil';
import type {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import type {AnonymousSchema} from 'schemas/SchemaConstants';
// TODO: Replace getTranslatedTagSchemaFromCache with a proper Apollo query or local state.
// See PR feedback: direct cache reads are an anti-pattern.
import {getTranslatedTagSchemaFromCache} from 'schemas/util/getTagSchemaFromCache';
import type {MappableField} from 'spreadsheet-import-wizard/shared/ColumnMapper';
import ColumnMapper from 'spreadsheet-import-wizard/shared/ColumnMapper';

import {MOLECULE_BUILT_IN_FIELDS} from './MoleculeFieldConstants';
import type {ModalAction, ModalState} from './moleculeImportReducer';
import useMoleculeSchemasPairing from './useMoleculeSchemasPairing';

interface ColumnMappingPaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

const FLAT_BUILT_IN_FIELDS: Array<MappableField> = [
  {id: MOLECULE_BUILT_IN_FIELDS.NAME, displayName: 'Molecule name', isRequired: true},
  {id: MOLECULE_BUILT_IN_FIELDS.STRUCTURE_SMILES, displayName: 'SMILES'},
  {id: MOLECULE_BUILT_IN_FIELDS.STRUCTURE_MOLFILE, displayName: 'Molfile'},
];

const OPTIONAL_ENTITY_TYPE_FIELDS: Array<MappableField> = [
  {
    id: MOLECULE_BUILT_IN_FIELDS.ALIAS,
    displayName: 'Alias',
    group: 'Optional entity types',
  },
];

export interface MergedFieldsResult {
  allFields: Array<MappableField>;
  expansion: Record<string, Array<string>>;
}

export function buildMergedFields(
  formSchema: AnonymousSchema | null | undefined,
  compoundSchema: AnonymousSchema | null | undefined,
  singleSchema: AnonymousSchema | null | undefined
): MergedFieldsResult {
  if (!formSchema && !compoundSchema && !singleSchema) {
    return {
      allFields: [...FLAT_BUILT_IN_FIELDS, ...OPTIONAL_ENTITY_TYPE_FIELDS],
      expansion: {},
    };
  }

  if (!formSchema || !compoundSchema) {
    const schema = singleSchema ?? formSchema ?? compoundSchema;
    if (!schema) {
      return {
        allFields: [...FLAT_BUILT_IN_FIELDS, ...OPTIONAL_ENTITY_TYPE_FIELDS],
        expansion: {},
      };
    }
    const schemaFields = ComputedFieldUtil.nonComputedFields(schema)
      .filter((field: SchemaField) => !field.isParentLink)
      .map((field: SchemaField) => ({
        id: field.apiId,
        displayName: field.name,
        isRequired: field.isRequired,
        group: 'Entity field',
      }));

    const fieldExpansion: Record<string, Array<string>> = {};
    for (const field of schemaFields) {
      fieldExpansion[field.id] = [field.id];
    }

    return {
      allFields: [...FLAT_BUILT_IN_FIELDS, ...OPTIONAL_ENTITY_TYPE_FIELDS, ...schemaFields],
      expansion: fieldExpansion,
    };
  }

  const formFields = ComputedFieldUtil.nonComputedFields(formSchema).filter(
    (f: SchemaField) => !f.isParentLink
  );
  const compoundFields = ComputedFieldUtil.nonComputedFields(compoundSchema).filter(
    (f: SchemaField) => !f.isParentLink
  );

  const fieldsByName: Record<string, {form?: SchemaField; compound?: SchemaField}> = {};
  for (const f of formFields) {
    fieldsByName[f.name] = {...fieldsByName[f.name], form: f};
  }
  for (const f of compoundFields) {
    fieldsByName[f.name] = {...fieldsByName[f.name], compound: f};
  }

  const entityFields: Array<MappableField> = [];
  const fieldExpansion: Record<string, Array<string>> = {};

  for (const [name, entry] of Object.entries(fieldsByName)) {
    if (entry.form && entry.compound) {
      const opaqueId = `__shared__${name}`;
      entityFields.push({
        id: opaqueId,
        displayName: name,
        group: 'Entity field',
        isRequired: entry.form.isRequired || entry.compound.isRequired,
      });
      fieldExpansion[opaqueId] = [entry.form.apiId, entry.compound.apiId];
    } else if (entry.form) {
      entityFields.push({
        id: entry.form.apiId,
        displayName: name,
        group: 'Entity field',
        isRequired: entry.form.isRequired,
      });
      fieldExpansion[entry.form.apiId] = [entry.form.apiId];
    } else if (entry.compound) {
      entityFields.push({
        id: entry.compound.apiId,
        displayName: name,
        group: 'Entity field',
        isRequired: entry.compound.isRequired,
      });
      fieldExpansion[entry.compound.apiId] = [entry.compound.apiId];
    }
  }

  return {
    allFields: [...FLAT_BUILT_IN_FIELDS, ...OPTIONAL_ENTITY_TYPE_FIELDS, ...entityFields],
    expansion: fieldExpansion,
  };
}

export default function ColumnMappingPane({state, dispatch}: ColumnMappingPaneProps): JSX.Element {
  const {pairingMap} = useMoleculeSchemasPairing(null);
  const pairing = state.schemaId ? pairingMap[state.schemaId] : undefined;

  const formSchema = pairing ? getTranslatedTagSchemaFromCache(pairing.formSchemaId) : null;
  const compoundSchema = pairing
    ? getTranslatedTagSchemaFromCache(pairing.compoundSchemaId)
    : state.compoundSchemaId
      ? getTranslatedTagSchemaFromCache(state.compoundSchemaId)
      : null;
  const singleSchema =
    !pairing && !compoundSchema && state.schemaId ? getTranslatedTagSchemaFromCache(state.schemaId) : null;

  const {allFields, expansion} = useMemo(
    () => buildMergedFields(formSchema, compoundSchema, singleSchema),
    [formSchema, compoundSchema, singleSchema]
  );

  const moleculeAutoDetect = useCallback((header: string, fields: Array<MappableField>): string | null => {
    if (/smiles/i.test(header)) {
      return MOLECULE_BUILT_IN_FIELDS.STRUCTURE_SMILES;
    }
    if (/molfile/i.test(header)) {
      return MOLECULE_BUILT_IN_FIELDS.STRUCTURE_MOLFILE;
    }
    if (/^name$/i.test(header)) {
      return MOLECULE_BUILT_IN_FIELDS.NAME;
    }
    if (/alias/i.test(header)) {
      return MOLECULE_BUILT_IN_FIELDS.ALIAS;
    }
    const normalized = header.toLowerCase().replace(/\s/g, '');
    return fields.find((f) => f.displayName.toLowerCase().replace(/\s/g, '') === normalized)?.id ?? null;
  }, []);

  const handleMappingChange = useCallback(
    (newMappings: Record<string, string>) => {
      dispatch({
        type: 'SET_COLUMN_MAPPINGS',
        payload: {columnMappings: newMappings, fieldExpansion: expansion},
      });
    },
    [dispatch, expansion]
  );

  const handleReset = useCallback(() => {
    const detected: Record<string, string> = {};
    const usedIds = new Set<string>();
    for (const header of state.headers ?? []) {
      const fieldId = moleculeAutoDetect(header, allFields);
      if (fieldId != null && !usedIds.has(fieldId)) {
        detected[header] = fieldId;
        usedIds.add(fieldId);
      }
    }
    dispatch({
      type: 'SET_COLUMN_MAPPINGS',
      payload: {columnMappings: detected, fieldExpansion: expansion},
    });
  }, [dispatch, expansion, state.headers, allFields, moleculeAutoDetect]);

  if (state.headers == null || state.schemaId == null) {
    return <Text>Upload a file and select a schema first.</Text>;
  }

  return (
    <Stack spacing="md">
      <Stack spacing={4} px="sm">
        <Text weight={600} size="sm">
          Map columns
        </Text>
        <Text size="sm" color="dimmed">
          Here&apos;s how we interpreted your spreadsheet headers. Review the column types to ensure they
          correspond to the headers from your spreadsheet.
        </Text>
        <Text size="sm" color="dimmed">
          Required mappings: Molecule name and at least one structure column (SMILES or Molfile).
        </Text>
      </Stack>
      <Divider />
      <ColumnMapper
        headers={state.headers}
        fields={allFields}
        mappings={state.columnMappings}
        onMappingChange={handleMappingChange}
        autoDetect={true}
        autoDetectFn={moleculeAutoDetect}
        onReset={handleReset}
      />
    </Stack>
  );
}
